import {
    graphql,
    useFragment,
    GraphQLTaggedNode,
    useRelayEnvironment,
    usePaginationFragment,
} from 'react-relay';
import {
    trackCarouselTileClick,
    trackCarouselImpressions,
} from './pdpRespSharedRefetchCarouselTracking';
import { hasKey } from 'dibs-ts-utils/exports/hasKey';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { flattenConnectionNodes } from '../../../utils/relayUtils';
import { usePaginationHooks } from '../../hooks/usePaginationHooks';
import { FC, useRef, useState, useEffect, useCallback, ReactNode, MouseEvent } from 'react';

//components
import { Carousel } from 'dibs-carousel';
import { FormattedMessage } from 'dibs-react-intl';
import { SeoLink } from 'dibs-elements/exports/SeoLink';
import {
    QuickViewProvider,
    QuickViewProviderChildrenProps,
} from '../../../global/QuickViewProvider/QuickViewProvider';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import { FavoritesProvider } from '../../../components/global/FavoriteIcons/FavoritesProvider';
import { PdpRespSharedCarouselItemTile } from '../PdpRespSharedCarouselItemTile/PdpRespSharedCarouselItemTile';
import { FavoritesProviderChildrenProps } from '../../../components/global/FavoriteIcons/FavoritesProviderChildrenProps';

// styles
import styles from './PdpRespSharedItemsRefetchCarousel.scss';

// types
import {
    CarouselItemsDataKey,
    FavoritesLocation,
    Item as CarouselItem,
} from './PdpRespSharedItemsRefetchCarouselConfig';
import { PdpRespSharedItemsRefetchCarousel_user$key as User } from './__generated__/PdpRespSharedItemsRefetchCarousel_user.graphql';
import { PdpRespSharedItemsRefetchCarouselConfig_YMAL_viewer$key } from './__generated__/PdpRespSharedItemsRefetchCarouselConfig_YMAL_viewer.graphql';
import { PdpRespSharedItemsRefetchCarouselConfig_sellerYMAL_viewer$key } from './__generated__/PdpRespSharedItemsRefetchCarouselConfig_sellerYMAL_viewer.graphql';

type Viewer =
    | PdpRespSharedItemsRefetchCarouselConfig_YMAL_viewer$key
    | PdpRespSharedItemsRefetchCarouselConfig_sellerYMAL_viewer$key;

type RenderItem = FC<{
    index: number;
    quickView: QuickViewProviderChildrenProps;
    favorites: FavoritesProviderChildrenProps;
}>;

const ITEMS_PER_PAGE = 5;

const userFragment = graphql`
    fragment PdpRespSharedItemsRefetchCarousel_user on User {
        serviceId
        ...PdpRespSharedCarouselItemTile_user
    }
`;

export const PdpRespSharedItemsRefetchCarousel: FC<{
    viewer: Viewer;
    dataTn?: string;
    dataKey: CarouselItemsDataKey;
    currency: string;
    title: ReactNode;
    user: User | null;
    trackingList: string;
    disableFavorites: boolean;
    viewerQuery: GraphQLTaggedNode;
    favoritesLocation: FavoritesLocation;
    viewAllLinkData?: {
        path: string;
        isLinkable: boolean;
        onClick: (e: MouseEvent) => void;
    } | null;
}> = ({
    title,
    dataTn,
    dataKey,
    currency,
    viewerQuery,
    trackingList,
    user: userRef,
    viewAllLinkData,
    viewer: viewerRef,
    favoritesLocation,
    disableFavorites = true,
}) => {
    const { data: viewer, loadNext } = usePaginationFragment(viewerQuery, viewerRef);
    const user = useFragment(userFragment, userRef);
    const carouselRef = useRef(null);
    const environment = useRelayEnvironment();
    const [carouselPage, setCarouselPage] = useState<number>(0);
    const [carouselIndex, setCarouselIndex] = useState<number>(0);
    const impressionTrackingRef = useRef<Record<number, boolean>>({});
    const [isCarouselVisible, setIsCarouselVisible] = useState<boolean>(false);
    const [shouldTrackImpressions, setShouldTrackImpressions] = useState<boolean>(true);
    const { onClickArrow, onCarouselReset, hasFetchedAllImages } = usePaginationHooks(loadNext, {
        label: trackingList,
    });
    const userId = user?.serviceId || null;

    useEffect(() => {
        if (userId) {
            setCarouselIndex(0);
            onCarouselReset();
        }
    }, [userId, onCarouselReset]);

    let items: CarouselItem[] = [];

    if (hasKey(viewer, dataKey)) {
        items = flattenConnectionNodes(viewer?.[dataKey]).filter(filterNulls) as CarouselItem[];
    }

    useEffect(() => {
        if (
            isCarouselVisible &&
            shouldTrackImpressions &&
            !impressionTrackingRef.current[carouselPage]
        ) {
            impressionTrackingRef.current[carouselPage] = true;

            const startIndex = carouselPage * ITEMS_PER_PAGE;
            trackCarouselImpressions(items.slice(startIndex, startIndex + ITEMS_PER_PAGE), {
                startingIndex: startIndex,
                list: trackingList,
            });

            setShouldTrackImpressions(false);
        }
    }, [carouselPage, isCarouselVisible, items, shouldTrackImpressions, trackingList]);

    const renderItem = useCallback<RenderItem>(
        ({ index, quickView, favorites }) => {
            const item = items[index];

            if (!item) {
                return null;
            }

            return (
                <PdpRespSharedCarouselItemTile
                    user={user}
                    item={item}
                    index={index}
                    viewer={viewer}
                    currency={currency}
                    quickView={quickView}
                    favorites={favorites}
                    onClick={() => trackCarouselTileClick(item, { list: trackingList, index })}
                />
            );
        },
        [items, user, viewer, currency, trackingList]
    );

    if (!items.length) {
        return null;
    }

    return (
        <div className={styles.container} ref={carouselRef} data-tn={dataTn}>
            <span className={styles.title}>{title}</span>
            {viewAllLinkData && (
                <SeoLink
                    className={styles.viewAllLink}
                    onClick={viewAllLinkData.onClick}
                    linkData={{
                        path: viewAllLinkData.path,
                        isLinkable: viewAllLinkData.isLinkable,
                    }}
                    dataTn="pdp-product-tile-carousel-view-all-link"
                >
                    <FormattedMessage
                        defaultMessage="View All"
                        id="PdpRespSharedItemsRefetchCarousel.viewAll"
                    />
                </SeoLink>
            )}
            <VisibilityTracker
                elementRef={carouselRef}
                onVisibilityChange={({ isVisible }) => setIsCarouselVisible(isVisible)}
            />
            <FavoritesProvider
                userId={userId}
                environment={environment}
                disable={disableFavorites}
                location={favoritesLocation}
                itemIds={items.map(node => node?.serviceId)}
            >
                {({ props: favorites }: { props: FavoritesProviderChildrenProps }) => (
                    <QuickViewProvider>
                        {({ quickView }) => (
                            <Carousel
                                isInfinite
                                dataTn={dataTn}
                                slideCarouselItems
                                step={ITEMS_PER_PAGE}
                                index={carouselIndex}
                                totalItems={items.length}
                                itemsPerPage={ITEMS_PER_PAGE}
                                onLeftArrowClick={() =>
                                    onClickArrow(() =>
                                        setShouldTrackImpressions(!hasFetchedAllImages)
                                    )
                                }
                                onRightArrowClick={() =>
                                    onClickArrow(() =>
                                        setShouldTrackImpressions(!hasFetchedAllImages)
                                    )
                                }
                                onPageChange={({ page }: { page: number }) => setCarouselPage(page)}
                                onIndexChange={({ index }: { index: number }) =>
                                    setCarouselIndex(index)
                                }
                                renderItem={({ index }: { index: number }) =>
                                    renderItem({ index, quickView, favorites })
                                }
                                classNames={{
                                    list: styles.list,
                                    item: styles.item,
                                    arrowLeft: `${styles.arrow} ${styles.arrowLeft}`,
                                    arrowRight: `${styles.arrow} ${styles.arrowRight}`,
                                }}
                                onCarouselReset={onCarouselReset}
                            />
                        )}
                    </QuickViewProvider>
                )}
            </FavoritesProvider>
        </div>
    );
};
